import type { FocusEventHandler, FormEvent } from 'react';
import { useRef } from 'react';
import { connectSearchBox } from 'react-instantsearch-dom';

import CloseIcon from '~/assets/icons/Close';
import SearchIcon from '~/assets/icons/Search';

const MAX_QUERY_LENGTH = 512;

type ConnectedProps = {
  currentRefinement: string;
  refine: (query: string) => void;
};

type Props = ConnectedProps & {
  isLargeActive: boolean;
  isLargeLayout: boolean;
  onFocus: FocusEventHandler<HTMLInputElement>;
  onReset: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  shouldFocusOnMount: boolean;
};

function inputWidthClassName(isLargeLayout: boolean, isLargeActive: boolean) {
  if (!isLargeLayout || isLargeActive) return 'w-full';

  return 'w-[70%] lg:w-full min-[1200px]:w-[70%]';
}

function submitPositionClassName(
  isLargeLayout: boolean,
  isLargeActive: boolean
) {
  if (!isLargeLayout) return 'left-[10px]';
  if (isLargeActive) return 'left-[6px] transition-[left] duration-300';

  return 'left-[69px] transition-[left] duration-300 lg:left-[9px] min-[1200px]:left-[89px]';
}

function resetPositionClassName(isLargeLayout: boolean) {
  if (isLargeLayout) return 'pr-[10px]';

  return 'pr-[15px] md:pr-5';
}

function SearchBox({
  currentRefinement,
  isLargeActive,
  isLargeLayout,
  onFocus,
  onReset,
  onSubmit,
  refine,
  shouldFocusOnMount
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleReset() {
    refine('');
    inputRef.current?.focus();
    onReset();
  }

  return (
    <div
      className={
        isLargeLayout ? 'lg:w-[200px] min-[1200px]:w-[270px]' : 'w-full'
      }
    >
      <form
        noValidate
        action=""
        role="search"
        className="relative"
        onSubmit={onSubmit}
        onReset={handleReset}
      >
        <input
          ref={inputRef}
          type="search"
          aria-label="Search"
          placeholder="Search"
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the box takes focus the moment the search panel opens
          autoFocus={shouldFocusOnMount}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          required
          maxLength={MAX_QUERY_LENGTH}
          value={currentRefinement}
          onChange={(event) => refine(event.currentTarget.value)}
          onFocus={onFocus}
          data-search-box-control
          className={`appearance-none rounded-[10px] border-none bg-white focus:outline-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden ${inputWidthClassName(isLargeLayout, isLargeActive)} ${
            isLargeLayout
              ? 'float-right hidden h-10 py-[10px] pl-9 pr-7 text-[14px] leading-5 text-[#949495] transition-[width] duration-300 lg:block'
              : 'h-[35px] py-5 pl-10 pr-[33px] text-[16px] leading-5 text-[#4d4d4f] md:pr-[38px]'
          }`}
        />
        <button
          type="submit"
          title="Submit your search query."
          data-search-box-control
          className={`absolute top-[11px] appearance-none px-[6px] py-px ${submitPositionClassName(isLargeLayout, isLargeActive)}`}
        >
          <SearchIcon width={16} height={16} stroke="#B7B7B8" />
        </button>
        {currentRefinement && (
          <button
            type="reset"
            title="Clear the search query."
            data-search-box-control
            className={`absolute right-0 top-0 flex h-10 appearance-none items-center ${resetPositionClassName(isLargeLayout)}`}
          >
            <CloseIcon width="10" height="10" stroke="#B7B7B8" />
          </button>
        )}
      </form>
    </div>
  );
}

export default connectSearchBox<Props>(SearchBox);
